import { Locator } from "@blast/foundations";
import { SharedBrickLocatorMap } from "../SharedBrick";
import { Cloudinary } from "@cloudinary/url-gen";
import { focusOn } from "@cloudinary/url-gen/qualifiers/gravity";
import { face } from "@cloudinary/url-gen/qualifiers/focusOn";
import { thumbnail } from "@cloudinary/url-gen/actions/resize";
import { FirebaseApp } from "firebase/app";
import { doc as docRef, getFirestore } from "firebase/firestore";
import { doc } from "rxfire/firestore";
import { Observable, of } from "rxjs";
import {
  catchError,
  distinctUntilChanged,
  map,
  shareReplay,
  switchMap,
} from "rxjs/operators";
import { AppConfig, DEFAULT_CLOUDINARY_FOLDER } from "@blast/models";
import { AuthService } from "./auth.service";

/** Builds Cloudinary assets for one season's folder */
class CloudinaryAssets {
  constructor(
    private readonly _cloudinary: Cloudinary,
    readonly folder: string
  ) {}

  getAvatar(publicId: string) {
    return this._cloudinary
      .image(`${this.folder}/${publicId}`)
      .resize(thumbnail().width(200).height(200).gravity(focusOn(face())));
  }

  getAvatarURL(publicId: string) {
    return this.getAvatar(publicId).toURL();
  }

  getVideo(publicId: string) {
    return this._cloudinary.video(`${this.folder}/${publicId}`);
  }

  getVideoURL(publicId: string) {
    return this.getVideo(publicId).toURL();
  }

  getVocalVideoId(blastId: string) {
    return `${this.folder}/${blastId}_vocal`;
  }

  getDanceVideoId(group: string) {
    return `${this.folder}/${group}_dance`;
  }
}

class CloudinaryService {
  private _assets: Observable<CloudinaryAssets>;

  constructor(app: FirebaseApp, authService: AuthService) {
    const cloudinary = new Cloudinary({
      cloud: {
        cloudName: "wchs-blast",
      },
    });
    const configDoc = docRef(getFirestore(app), "info/config");

    // info/config is only readable once signed in
    this._assets = authService.loggedIn.pipe(
      switchMap((loggedIn) =>
        loggedIn
          ? doc(configDoc).pipe(
              map(
                (doc) => (doc.data() as AppConfig | undefined)?.cloudinaryFolder
              ),
              catchError(() => of(undefined))
            )
          : of(undefined)
      ),
      map((folder) => folder || DEFAULT_CLOUDINARY_FOLDER),
      distinctUntilChanged(),
      map((folder) => new CloudinaryAssets(cloudinary, folder)),
      shareReplay(1)
    );
  }

  /** Asset builder for the configured folder, re-emitted when it changes */
  get assets(): Observable<CloudinaryAssets> {
    return this._assets;
  }
}

export async function initializeCloudinaryService(
  locator: Locator<SharedBrickLocatorMap>
) {
  const [app, authService] = await locator.getAll(["App", "AuthService"]);
  return new CloudinaryService(app, authService);
}

export type { CloudinaryService, CloudinaryAssets };
